import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../style/landing-page.module.css';
import { login, normalizeEmail, setSessionID } from '../redux/action-creators';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { notification } from 'antd';
import axios from 'axios';
import PasswordInput from './password-input';

const LandingPage = () => {
  const [activeCard, setActiveCard] = useState('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [signInPending, setSignInPending] = useState(false);

  const sessionID = useSelector((state) => state.sessionID);
  const userInfo = useSelector((state) => state.userInfo);
  const dispatch = useDispatch();
  const history = useHistory();

  const resetPassword = useCallback(async () => {
    const data = {
      code: resetCode,
      newPassword: password
    };

    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return axios
      .post(process.env.REACT_APP_API_URL + '/user/resetPassword', data, config)
      .then((response) => {
        setPassword('');
        setActiveCard('signIn');
      })
      .catch((error) => {
        console.log(error);
      });
  }, [password, resetCode]);

  const sendPasswordResetRequest = useCallback(async () => {
    const data = {
      email: normalizeEmail(email)
    };

    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return axios
      .post(process.env.REACT_APP_API_URL + '/user/resetPasswordRequest', data, config)
      .then((response) => {
        setActiveCard('resetPassword_b');
        notification.success({
          description: 'The password reset code has been sent to your email.'
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, [email]);

  const resetPassword_a = useMemo(() => {
    return (
      <div className={styles['card']}>
        <div className={styles['input-container']}>
          <input
            className={styles['input']}
            key="resetPassword_a_email"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') sendPasswordResetRequest();
            }}
            placeholder="Email"
            type="email"
          />
        </div>
        <div className={styles['button-container']}>
          <button className={styles['button']} onClick={() => sendPasswordResetRequest()}>
            Send Code
          </button>
        </div>
        <div className={styles['action-container']}>
          <div
            onClick={() => {
              setPassword('');
              setActiveCard('signIn');
            }}
          >
            <label className={styles['action']}>Sign In</label>
          </div>
        </div>
      </div>
    );
  }, [sendPasswordResetRequest]);

  const resetPassword_b = useMemo(() => {
    return (
      <div className={styles['card']}>
        <div className={styles['input-container']}>
          <PasswordInput
            inputClassName={styles['input']}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') resetPassword();
            }}
            placeholder="New Password"
            value={password}
            wrapperClassName={styles['password-input-wrapper']}
          />
          <input
            className={styles['input']}
            key="resetPassword_b_code"
            onChange={(e) => setResetCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') resetPassword();
            }}
            placeholder="Reset Code"
            type="resetCode"
          />
        </div>
        <div className={styles['button-container']}>
          <button className={styles['button']} onClick={() => resetPassword()}>
            Reset
          </button>
        </div>
        <div className={styles['action-container']}>
          <div
            onClick={() => {
              setPassword('');
              setActiveCard('signIn');
            }}
          >
            <label className={styles['action']}>Sign In</label>
          </div>
        </div>
      </div>
    );
  }, [resetPassword, password]);

  const handleSignIn = useCallback(async () => {
    if (signInPending) return;
    setSignInPending(true);
    try {
      await dispatch(login(email, password));
    } finally {
      setSignInPending(false);
    }
  }, [dispatch, email, password, signInPending]);

  const signInCard = useMemo(() => {
    return (
      <div className={styles['card']}>
        <div className={styles['input-container']}>
          <input
            autoComplete="email"
            className={styles['input']}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSignIn();
            }}
            placeholder="Email"
            type="email"
          />
          <PasswordInput
            autoComplete="current-password"
            inputClassName={styles['input']}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSignIn();
            }}
            placeholder="Password"
            value={password}
            wrapperClassName={styles['password-input-wrapper']}
          />
        </div>
        <div className={styles['button-container']}>
          <button className={styles['button']} disabled={signInPending} onClick={handleSignIn}>
            Sign In
          </button>
        </div>
        <div className={styles['spacer']}></div>
        <div className={styles['action-container']}>
          <button
            className={styles['forgot-password']}
            onClick={() => {
              setPassword('');
              setActiveCard('resetPassword_a');
            }}
          >
            Forgot Password?
          </button>
        </div>
      </div>
    );
  }, [handleSignIn, signInPending, password]);

  const getActiveCard = useCallback(() => {
    switch (activeCard) {
      case 'resetPassword_a':
        return resetPassword_a;
      case 'resetPassword_b':
        return resetPassword_b;
      case 'signIn':
        return signInCard;
      default:
        return signInCard;
    }
  }, [activeCard, resetPassword_a, resetPassword_b, signInCard]);

  useEffect(() => {
    // forward to appropriate page when token is present
    if (userInfo.kind === 'Developer') history.push('/developer/batteries');
    // Administration starts from a battery (PIN-210); the session list is a
    // resume-only page with no top-nav entry of its own.
    else if (userInfo.kind === 'Examiner') history.push('/examiner/batteries');
    else if (userInfo.kind === 'Administrator') history.push('/developer/batteries');
  }, [history, userInfo]);

  return (
    <div className={styles['container-a']}>
      <div className={styles['title-container']}>
        <label className={styles['logo']}>Schoolhouse Educational Services Assessments</label>
      </div>
      <div className={styles['container-b']}>
        <div className={styles['column-left']}>
          <div className={styles['card']}>
            <div className={styles['info-text-container']}>
              <p className={styles['info-text']}>
                Registration is only for individuals who are qualified to administer and interpret
                educational and psychological tests. Approval of your registration might take up to
                48 hours.
              </p>
              <p className={styles['info-text']}>
                If you are a teacher, parent, or student who has been asked to complete a form, do
                not register. To access the form you are to complete, contact the person who invited
                you to complete the form.
              </p>
              <p className={styles['info-text']}>
                For assistance with registration or other questions email{' '}
                <a
                  className={styles['info-link']}
                  href="mailto:CustomerService@SchoolhouseEducationalServices.com"
                >
                  CustomerService@SchoolhouseEducationalServices.com
                </a>{' '}
                or call 608-487-8282.
              </p>
              <p className={styles['info-text']}>
                All content and products on this website are copyrighted by Schoolhouse Educational
                Services, Inc.
              </p>
              <p className={styles['info-text']}>
                <a
                  className={styles['info-link']}
                  href={`${process.env.PUBLIC_URL}/user-agreement.pdf`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  User Agreement
                </a>{' '}
                &nbsp;|&nbsp;{' '}
                <a
                  className={styles['info-link']}
                  href={`${process.env.PUBLIC_URL}/privacy-policy.pdf`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Privacy Policy
                </a>
              </p>
              <div className={styles['button-container']}>
                <button className={styles['button']} onClick={() => history.push('/register')}>
                  Register
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className={styles['column-right']}>
          {getActiveCard()}
          <div className={styles['card']}>
            <div className={styles['input-container']}>
              <input
                autoCapitalize="none"
                autoComplete="off"
                autoCorrect="off"
                className={styles['input']}
                onChange={(e) => dispatch(setSessionID(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') history.push(`/examinee/test/${sessionID}`);
                }}
                placeholder="Session ID"
                spellCheck={false}
                type="text"
              />
            </div>
            <div className={styles['button-container']}>
              <button
                className={styles['button']}
                onClick={() => history.push(`/examinee/test/${sessionID}`)}
              >
                Join Session
              </button>
            </div>
            <div className={styles['action-container']} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
